import { Assessment } from '@seen/core/models/assessments/assessment'
import type { ParticipantNote } from '@seen/core/models/participantNote'
import type { ColumnDef } from '@tanstack/react-table'
import { orderBy } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useParticipantNoteDrafts } from '#src/hooks/useParticipantNoteDrafts.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import {
  AssessmentActionsCell,
  AssessmentLastUpdatedCell,
  AssessmentParticipantCell,
  AssessmentTitleCell,
} from '#src/pages/AssessmentDraftCells.tsx'
import {
  ParticipantNoteActionsCell,
  ParticipantNoteLastUpdatedCell,
  ParticipantNoteParticipantCell,
  ParticipantNoteTitleCell,
} from '#src/pages/ParticipantNoteDraftCells.tsx'

export function DraftsPage(): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const practitioner = useCurrentPractitioner()

  const { data: assessments } = useSearch(Assessment, {
    ...Assessment.searchQuery({ practitioner: practitioner.reference }),
    'attester:missing': true,
  })

  const { data: participantNotes } = useParticipantNoteDrafts(
    practitioner.reference
  )

  const columns: ColumnDef<Assessment | ParticipantNote>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) =>
          row.original instanceof Assessment ? (
            <AssessmentTitleCell assessment={row.original} />
          ) : (
            <ParticipantNoteTitleCell participantNote={row.original} />
          ),
      },
      {
        accessorKey: 'participant',
        header: 'Participant',
        cell: ({ row }) =>
          row.original instanceof Assessment ? (
            <AssessmentParticipantCell assessment={row.original} />
          ) : (
            <ParticipantNoteParticipantCell participantNote={row.original} />
          ),
      },
      {
        accessorKey: 'lastUpdated',
        header: 'Last updated',
        cell: ({ row }) =>
          row.original instanceof Assessment ? (
            <AssessmentLastUpdatedCell assessment={row.original} />
          ) : (
            <ParticipantNoteLastUpdatedCell participantNote={row.original} />
          ),
      },
      ...(!isReadOnly
        ? [
            {
              id: 'actions',
              cell: ({
                row,
              }: {
                row: { original: Assessment | ParticipantNote }
              }) =>
                row.original instanceof Assessment ? (
                  <AssessmentActionsCell assessment={row.original} />
                ) : (
                  <ParticipantNoteActionsCell participantNote={row.original} />
                ),
            },
          ]
        : []),
    ],
    [isReadOnly]
  )

  const tableData = useMemo(() => {
    return orderBy(
      [...(assessments ?? []), ...(participantNotes ?? [])],
      ['lastUpdated'],
      ['desc']
    )
  }, [assessments, participantNotes])

  if (!assessments || !participantNotes) {
    return <Loader />
  }

  return (
    <PageLayout title="Drafts">
      <DataTable columns={columns} data={tableData} />
    </PageLayout>
  )
}
